import {createContext} from "react";

import type {MainDialogState} from "../pages/app/hooks/useMainDialog";
import type {OpenChatParams} from "../types";

/**
 * Возврат в приложение после внешней страницы оплаты (T-Bank).
 * Уход на оплату — полная выгрузка SPA (window.location.href), поэтому перед
 * редиректом состояние сериализуется в localStorage, а PaymentConfirmPage
 * возвращает его странице через location.state — та восстанавливает стек
 * модалок и продолжает поток (см. usePaymentResume).
 */

export type ChatSessionSnapshot = OpenChatParams & {conversationId: string};

export interface PaymentResumeSnapshot {
    /** Кто уходил на оплату — снапшот другого аккаунта не восстанавливаем. */
    userId?: string;
    /** Маршрут, с которого ушли на оплату ("/app", "/app/chats"). */
    route: string;
    /** Стек модалок на момент ухода — дескрипторы useMainDialog сериализуемы. */
    dialogStack: MainDialogState[];
    /** Открытая сессия чата (страница Chats), чтобы вернуться в тот же диалог. */
    chatSession?: ChatSessionSnapshot;
    /** kind созданного платежа — по нему страница решает, какой поток продолжать. */
    paymentKind: string;
    /**
     * id платежа, ради которого снапшот сохранён. Обязателен: без него любой более
     * поздний успешный платёж «завершал» брошенную ранее покупку — например, оплата
     * суперлайков открывала неоплаченный детальный анализ.
     */
    paymentId: string;
    savedAt: number;
}

/** location.state, с которым PaymentConfirmPage возвращает на страницу. */
export interface PaymentResumeState {
    snapshot: PaymentResumeSnapshot;
    paid: boolean;
}

const STORAGE_KEY = "rl.payment-resume";
/** Снапшот старше — не восстанавливаем: состояние почти наверняка неактуально. */
const TTL_MS = 2 * 60 * 60 * 1000;

export function isAnalysisPaymentKind(kind: string): boolean {
    return kind === "analysis" || kind === "analysis_unlimited";
}

export function savePaymentResumeSnapshot(snapshot: Omit<PaymentResumeSnapshot, "savedAt">): void {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({...snapshot, savedAt: Date.now()}));
    } catch {
        // Приватный режим/переполнение — просто вернёмся без восстановления.
    }
}

/** Смена/выход аккаунта — чужой снапшот не должен пережить логаут. */
export function clearPaymentResumeSnapshot(): void {
    try {
        localStorage.removeItem(STORAGE_KEY);
    } catch {
        // Недоступный storage — значит, и снапшота там нет.
    }
}

/** Прочитать снапшот, НЕ удаляя; null — если его нет, он битый или протух. */
export function peekPaymentResumeSnapshot(): PaymentResumeSnapshot | null {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as PaymentResumeSnapshot;
        if (typeof parsed?.route !== "string" || !Array.isArray(parsed.dialogStack)) return null;
        if (Date.now() - (parsed.savedAt ?? 0) > TTL_MS) return null;
        return parsed;
    } catch {
        return null;
    }
}

/**
 * Прочитать и удалить снапшот, относящийся к указанному платежу.
 * Снапшот от другого платежа не отдаём и НЕ удаляем — пользователь ещё может
 * вернуться по своему платежу и продолжить прерванный поток.
 */
export function consumePaymentResumeSnapshot(paymentId: string | null): PaymentResumeSnapshot | null {
    const parsed = peekPaymentResumeSnapshot();
    if (!parsed) return null;
    if (!paymentId || parsed.paymentId !== paymentId) return null;
    clearPaymentResumeSnapshot();
    return parsed;
}

/** Окно, в котором «назад» из оплаты считается возвратом с неё. */
export const BACK_RESTORE_TTL_MS = 30 * 60 * 1000;

/** Текущая загрузка документа — переход «назад/вперёд» (а не новый заход). */
export function isBackForwardNavigation(): boolean {
    try {
        const nav = performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined;
        return nav?.type === "back_forward";
    } catch {
        return false;
    }
}

const BACK_DONE_KEY = "rl.payment-resume-back-done";

/** paymentId снапшота, который уже восстановлен по «назад» (в рамках вкладки). */
export function readBackRestoredPaymentId(): string | null {
    try {
        return sessionStorage.getItem(BACK_DONE_KEY);
    } catch {
        return null;
    }
}

export function markBackRestored(paymentId: string): void {
    try {
        sessionStorage.setItem(BACK_DONE_KEY, paymentId);
    } catch {
        // Без sessionStorage восстановление может повториться — не критично.
    }
}

const pathOf = (route: string) => route.split("?")[0];

/**
 * «Назад» из оплаты без paymentId: вернуть снапшот, если это back_forward на тот
 * же маршрут (по pathname), снапшот свежий, принадлежит этому пользователю и ещё
 * не восстанавливался по «назад». Иначе null.
 */
export function pickBackNavigationSnapshot(params: {
    isBackForward: boolean;
    snapshot: PaymentResumeSnapshot | null;
    userId?: string;
    currentRoute: string;
    now: number;
    alreadyRestoredPaymentId?: string | null;
}): PaymentResumeSnapshot | null {
    const {isBackForward, snapshot, userId, currentRoute, now, alreadyRestoredPaymentId} = params;
    if (!isBackForward || !snapshot) return null;
    if (alreadyRestoredPaymentId && alreadyRestoredPaymentId === snapshot.paymentId) return null;
    if (now - snapshot.savedAt > BACK_RESTORE_TTL_MS) return null;
    if (snapshot.userId && userId && snapshot.userId !== userId) return null;
    if (pathOf(snapshot.route) !== pathOf(currentRoute)) return null;
    return snapshot;
}

/**
 * Сохранить снапшот перед уходом на оплату ((kind, paymentId) → localStorage).
 * Контекст даёт его окнам, которые сами не знают стек модалок страницы
 * (платное обновление разбора внутри DetailedAnalysisModal): провайдер стоит
 * в AppLayout и пишет тот же снапшот, что и остальные платные окна. Вне
 * провайдера (страницы без AppLayout-стека) окно пишет снапшот по умолчанию.
 */
export type PaymentSnapshotSaver = (kind: string, paymentId: string) => void;
export const PaymentSnapshotSaverContext = createContext<PaymentSnapshotSaver | null>(null);

/**
 * Метка «платное обновление разбора ушло в банк». После возврата окно разбора
 * открывается заново и показывает прежнюю версию, а перегенерацию запускает
 * вебхук уже ПОСЛЕ оплаты. По метке окно знает, что надо ждать новую версию
 * (а не предлагать «Обновить разбор» ещё раз и платить второй раз).
 */
export interface AnalysisRefreshMarker {
    paymentId: string;
    peerId: string;
    method: string;
    /** snapshotAt, который видел человек до оплаты: сменился — обновление уже готово. */
    snapshotAt: string;
    savedAt: number;
}

const REFRESH_MARKER_KEY = "rl.analysis-refresh-pending";
const REFRESH_MARKER_TTL_MS = 30 * 60 * 1000;

export function saveAnalysisRefreshMarker(marker: Omit<AnalysisRefreshMarker, "savedAt">): void {
    try {
        localStorage.setItem(REFRESH_MARKER_KEY, JSON.stringify({...marker, savedAt: Date.now()}));
    } catch {
        // Без storage вернёмся без ожидания: человек увидит прежнюю версию, потом новую при обновлении.
    }
}

/** Прочитать и удалить метку для этого собеседника и метода; чужую/протухшую не отдаём. */
export function takeAnalysisRefreshMarker(peerId: string, method: string, now = Date.now()): AnalysisRefreshMarker | null {
    try {
        const raw = localStorage.getItem(REFRESH_MARKER_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as AnalysisRefreshMarker;
        if (now - (parsed?.savedAt ?? 0) > REFRESH_MARKER_TTL_MS) {
            localStorage.removeItem(REFRESH_MARKER_KEY);
            return null;
        }
        if (parsed.peerId !== peerId || parsed.method !== method || typeof parsed.paymentId !== "string") return null;
        localStorage.removeItem(REFRESH_MARKER_KEY);
        return parsed;
    } catch {
        return null;
    }
}
